import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import queryString from "query-string";
import { useNavigate, useLocation } from "react-router-dom";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ButtonDefault from "components/Buttons/ButtonDefault";
import ButtonPrimary from "components/Buttons/ButtonPrimary";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SpinnerCentered from "components/Loader/SpinnerCentered";

import {
  saveCompany,
  httpCompleteStripeAccount,
  httpRetriveStripeAccount,
} from "services/http/companies";
import CompanyPaymentForm from "components/CompanyPaymentForm/CompanyPaymentForm";
import { green } from "@mui/material/colors";

const useStyles = makeStyles((theme) => ({
  content: { padding: 48 },
  title: { paddingBottom: 32 },
  chip: {
    backgroundColor: "#F0F0F0",
    paddingTop: 16,
    paddingBottom: 16,
    paddingLeft: 32,
    paddingRight: 32,
    borderRadius: "8px",
  },
  chipActive: { color: theme.palette.success.main },
  chipInactive: { color: theme.palette.error.main },
  buttonLink: { textDecoration: "none" },
}));

const CompanyPayment = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { myCompany, getMyCompany, users } = props;

  const usersRef = useRef(users);
  usersRef.current = users;

  const [values, setValues] = useState({});
  const [stripeConnectUrl, setStripeConnectUrl] = useState("");
  const [loadingStripe, setLoadingStripe] = useState(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const retriveStripeAccount = useCallback(() => {
    setLoadingStripe(true);
    httpRetriveStripeAccount().then(
      (response) => {
        setValues(response);
        setLoadingStripe(false);
      },
      (_errors) => {
        setLoadingStripe(false);
      },
    );
  }, []);

  const handleGetMyCompany = useCallback(() => {
    setLoading(true);
    getMyCompany().then(
      (response) => {
        const u = usersRef.current;
        // Stripe connect url
        let client_id = "";
        if (process?.env?.REACT_APP_ENV == "production") {
          // Production client_key
          console.log("Production client_key");
          client_id = "ca_GitlA9I6Q3J2D3yROUszLlZeAyvzeWVL";
        } else {
          // Test client_key
          console.log("Test client_key");
          client_id = "ca_GitlkbafxCRiSHJnjJjVlrwk6V5Qoc1z";
        }

        const stripe_user_email = u?.data?.me?.email;
        let stripe_user_phone = "";
        if (u?.data?.me?.phone) {
          stripe_user_phone = u?.data?.me?.phone;
          stripe_user_phone = stripe_user_phone.replace("+358", "");
        }
        const stripe_user_first_name = u?.data?.me?.first_name;
        const stripe_user_last_name = u?.data?.me?.last_name;

        const stripe_user_business_name = response?.name;
        const stripe_user_tax_id = response?.business_id;

        let redirect_uri = "";
        if (process?.env?.REACT_APP_ENV == "production") {
          redirect_uri = "https://app.opentaxi.fi/dashboard/company/payment";
        } else if (process?.env?.REACT_APP_ENV == "dev") {
          redirect_uri = "https://dev.opentaxi.fi/dashboard/company/payment";
        } else {
          redirect_uri = "http://localhost:3001/dashboard/company/payment";
        }

        setStripeConnectUrl(
          "https://connect.stripe.com/oauth/authorize?response_type=code&client_id=" +
            client_id +
            "&scope=read_write&redirect_uri=" +
            redirect_uri +
            "&stripe_user[email]=" +
            stripe_user_email +
            "&stripe_user[phone_number]=" +
            stripe_user_phone +
            "&stripe_user[business_name]=" +
            stripe_user_business_name +
            "&stripe_user[tax_id]=" +
            stripe_user_tax_id +
            "&stripe_user[first_name]=" +
            stripe_user_first_name +
            "&stripe_user[last_name]=" +
            stripe_user_last_name +
            "&suggested_capabilities[]=card_payments&assert_capabilities[]=card_payments",
        );

        if (response.stripe_user_id) {
          retriveStripeAccount();
        }
        setLoading(false);
      },
      (_errors) => {
        setLoading(false);
      },
    );
  }, [getMyCompany, retriveStripeAccount]);

  const handleCompleteStripe = useCallback(
    (code) => {
      setLoadingStripe(true);
      httpCompleteStripeAccount(code).then(
        (_response) => {
          enqueueSnackbar("Maksutiedot liitetty onnistuneesti!", {
            variant: "success",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 2000,
          });

          setLoadingStripe(false);
          handleGetMyCompany();
          navigate("/dashboard/company/payment?success");
        },
        (_errors) => {
          enqueueSnackbar(
            "Virhe maksutietojen liittämisessä, yritä uudelleen tai ota yhteys tukeen.",
            {
              variant: "error",
              anchorOrigin: { vertical: "bottom", horizontal: "center" },
              autoHideDuration: 2000,
            },
          );

          setLoadingStripe(false);

          // refresh view
          navigate("/dashboard/company/payment?error");
        },
      );
    },
    [enqueueSnackbar, navigate, handleGetMyCompany],
  );

  const userStripeKey = [
    users?.data?.me?.email ?? "",
    users?.data?.me?.phone ?? "",
    users?.data?.me?.first_name ?? "",
    users?.data?.me?.last_name ?? "",
  ].join("\0");

  useEffect(() => {
    handleGetMyCompany();
  }, [handleGetMyCompany, userStripeKey]);

  useEffect(() => {
    if (users?.data?.me?.status && users?.data?.me?.status < 100) {
      navigate("/dashboard/company/payment?error");
    }
  }, [users, navigate]);

  useEffect(() => {
    const keys = queryString.parse(location.search);

    if (keys.state) {
    }

    if (keys.code) {
      handleCompleteStripe(keys.code);
    }

    if (keys.scope) {
    }
  }, [location.search, handleCompleteStripe]);

  const formatValues = (_values) => {
    return { ..._values };
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (myCompany?.stripe_user_id == null) {
      return;
    }

    setSaving(true);

    const request = {
      data: {
        id: values.id,
        name: values.name,
        business_id: values.business_id,
      },
    };

    saveCompany(request).then(
      (response) => {
        setValues(formatValues(response));

        enqueueSnackbar("Tallennettu", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        setSaving(false);
      },
      (_errors) => {
        enqueueSnackbar("Tallennus epäonnistui", {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        setSaving(false);
      },
    );
  };

  const handleCancel = () => {
    navigate("/dashboard/start");
  };

  // Stripe account fields are read-only; changes are made in Stripe, not here.
  const canSave = false;

  return (
    <Paper className={classes.content} elevation={0} square>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4">{t("Maksutiedot")}</Typography>
      </Box>

      {loading || loadingStripe ? (
        <SpinnerCentered />
      ) : (
        <Grid
          container
          direction="row"
          spacing={4}
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Grid size={{ xs: 12, sm: 12, md: 7, lg: 6 }}>
            {!myCompany?.stripe_user_id ? (
              <>
                <Box sx={{ mb: "64px" }}>
                  <a href={stripeConnectUrl} style={{ textDecoration: "none" }}>
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Badge badgeContent={"!"} color="error">
                        <Box
                          sx={{
                            color: "error.main",
                            bgcolor: "background.panel",
                            p: 2,
                          }}
                        >
                          {t("Tilisi vaatii lisätietoja")}
                        </Box>
                      </Badge>
                    </Box>
                  </a>

                  <Typography>
                    {t("Siirry")}&nbsp;
                    <a href={stripeConnectUrl} className={classes.buttonLink}>
                      {t("lisäämään")}
                    </a>
                    &nbsp;
                    {t(
                      "puuttuvat maksutiedot, jotta maksutilisi saadaan aktiiviseksi.",
                    )}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4">
                    {t("Katso tästä video ohje miten tili luodaan")}:
                  </Typography>
                </Box>

                <a
                  href="https://www.youtube.com/watch?v=C_ohwKS2UDU"
                  className={classes.buttonLink}
                >
                  https://www.youtube.com/watch?v=C_ohwKS2UDU
                </a>
              </>
            ) : null}

            {/*
                <Box sx={{ mb: "32px" }}>
                  <a href={stripeConnectUrl} className={classes.buttonLink}>
                    <Badge badgeContent={"!"} color="error">
                      <ButtonPrimary type="button">
                          {!myCompany?.stripe_user_id ? t('AKTIVOI STRIPE MAKSUPALVELU') : t('MAKSUPALVELU ODOTTAA VAHVISTUSTA')}
                      </ButtonPrimary>
                    </Badge>
                  </a>
                </Box>
              */}

            {myCompany?.stripe_user_id && values?.charges_enabled === false ? (
              <Box sx={{ mb: "32px" }}>
                <Typography>
                  {t(
                    "Odottaa, että Stripe vahvistaa maksutilinne. Jos vahvistusta ei tule viimeistään muutaman päivän sisällä tarkistathan, että kaikki vaaditut tiedot on täytetty. Ongelmatilanteissa ota yhteyttä support@opentaxi.fi",
                  )}
                </Typography>
              </Box>
            ) : null}

            <form onSubmit={handleSubmit} noValidate>
              {myCompany?.stripe_user_id ? (
                <Box sx={{ mb: "32px" }}>
                  <CompanyPaymentForm values={values} />
                </Box>
              ) : null}

              {myCompany?.stripe_user_id ? (
                <Grid size={{ xs: 12 }}>
                  <Grid
                    container
                    direction="row"
                    sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
                  >
                    <Grid size={12}>
                      <ButtonDefault onClick={handleCancel}>
                        {t("Peruuta")}
                      </ButtonDefault>
                    </Grid>
                    <Grid size={12}>
                      <ButtonPrimary
                        loading={saving}
                        type="submit"
                        disabled={!canSave || saving}
                      >
                        {t("Tallenna ja päivitä")}
                      </ButtonPrimary>
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
            </form>
          </Grid>

          {myCompany?.stripe_user_id && values?.charges_enabled ? (
            <Grid size={{ xs: 12, sm: 12, md: 5, lg: 3, xl: 2 }}>
              <Fragment>
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={t("STRIPE TUNNUS AKTIIVINEN")}
                    className={`${classes.chip} ${classes.chipActive}`}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    sx={{ justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Grid size={12}>
                      <Typography variant="subtitle">
                        {t("Maksujen vastaanotto")}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      {values?.charges_enabled ? (
                        <CheckIcon style={{ color: green[500] }} />
                      ) : (
                        <Fragment>
                          <CloseIcon color="error" />
                        </Fragment>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle">
                    {t("Maksutoiminnot")}
                  </Typography>
                  <Grid
                    container
                    direction="row"
                    spacing={2}
                    sx={{ justifyContent: "space-between", alignItems: "center" }}
                  >
                    <Grid size={12}>
                      <Typography>{t("Korttimaksut")}</Typography>
                    </Grid>
                    <Grid size={12}>
                      {values?.capabilities?.card_payments === "active" ? (
                        <CheckIcon style={{ color: green[500] }} />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                    </Grid>

                    {/*
                      <Grid size={12}>
                        <Typography>{t("Tilisiirto")}</Typography>
                      </Grid>
                      <Grid size={12}>
                      { values?.capabilities?.transfers === "active" ? (
                        <CheckIcon style={{ color: green[500] }} />
                      ) : (
                        <CloseIcon color="error" />
                      )}
                      </Grid>
                      */}
                  </Grid>
                </Box>

                {/*
                  <a href={stripeConnectUrl} className={classes.buttonLink}>
                    {t('Päivitä Stripe maksutietoja')}
                  </a>
                  */}
              </Fragment>
            </Grid>
          ) : null}
        </Grid>
      )}

      {/*
        <Fragment>
        { users?.data?.me?.status == 100 ? (
          <Fragment>
            <a href={stripeConnectUrl} className={classes.buttonLink}>
              <Badge badgeContent={"!"} color="error">
                <ButtonPrimary type="button">
                    {!myCompany?.stripe_user_id ? t('AKTIVOI STRIPE MAKSUPALVELU') : t('MAKSUPALVELU ODOTTAA VAHVISTUSTA')}
                </ButtonPrimary>
              </Badge>
            </a>
          </Fragment>
        ) : (
          <Typography>{t('Vain yrityksen admin voi lisätä maksutiedot')}</Typography>
        )}
        </Fragment>
      
      </Fragment>
      */}
    </Paper>
  );
};

export default CompanyPayment;
